import { useState, useCallback } from 'react';
import { Asset } from '@rts';
import useMetadataStore from '@/store/useMetadataStore';
import { GripVertical, Plus, X, Loader2, Layers } from 'lucide-react';
import { useT } from '@/i18n';
import './ThemeStackEditor.css';

/**
 * ThemeStackEditor - Drag-and-drop editor for Theme Stack v2.0
 *
 * Allows users to compose multiple themes in a stack.
 * First theme = highest priority, last = fallback (usually 'base')
 *
 * @example
 * Stack: [halloween, dark, base]
 * - halloween overrides → dark overrides → base (fallback)
 */
export default function ThemeStackEditor({ onStackChange }) {
  const t = useT();
  const [isLoading, setIsLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Get current stack from Asset
  const [stack, setStack] = useState(() => Asset.getStack());

  // Get all available themes from metadata
  const metadataThemes = useMetadataStore((state) => state.metadata?.themes) || [];

  // Themes not in the stack
  const availableThemes = metadataThemes.filter(
    (theme) => !stack.includes(theme.slug)
  );

  // Apply stack changes
  const applyStack = useCallback(async (newStack) => {
    if (newStack.length === 0) return;

    setIsLoading(true);
    try {
      await Asset.loadStack(newStack);
      setStack([...newStack]);
      onStackChange?.(newStack);
    } catch (error) {
      console.error('[ThemeStackEditor] Failed to apply stack:', error);
    } finally {
      setIsLoading(false);
    }
  }, [onStackChange]);

  // Add theme to stack
  const addToStack = useCallback((slug) => {
    if (stack.includes(slug)) return;

    // Insert before 'base' if base is last
    const newStack = [...stack];
    const baseIndex = newStack.indexOf('base');
    if (baseIndex === newStack.length - 1 && slug !== 'base') {
      newStack.splice(baseIndex, 0, slug);
    } else {
      newStack.push(slug);
    }

    applyStack(newStack);
  }, [stack, applyStack]);

  // Remove theme from stack (can't remove last theme)
  const removeFromStack = useCallback((slug) => {
    if (stack.length <= 1) return;
    if (slug === 'base' && stack.length === 1) return;

    const newStack = stack.filter((s) => s !== slug);
    applyStack(newStack);
  }, [stack, applyStack]);

  // Drag handlers
  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      return;
    }

    const newStack = [...stack];
    const [moved] = newStack.splice(draggedIndex, 1);
    newStack.splice(targetIndex, 0, moved);

    setDraggedIndex(null);
    applyStack(newStack);
  }, [draggedIndex, stack, applyStack]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  // Get theme info from metadata
  const getThemeInfo = (slug) => {
    return metadataThemes.find((t) => t.slug === slug) || {
      slug,
      nameKey: slug,
      preview: { primary: '#888', secondary: '#666', bg: '#333' },
    };
  };

  return (
    <div className="theme-stack-editor">
      {/* Header */}
      <div className="theme-stack-header">
        <Layers size={16} />
        <span>{t('config.theme_stack_title') || 'Theme Stack'}</span>
        {isLoading && <Loader2 size={14} className="theme-stack-loading" />}
      </div>

      {/* Current Stack */}
      <div className="theme-stack-list">
        {stack.map((slug, index) => {
          const theme = getThemeInfo(slug);
          const isBase = slug === 'base';
          const canRemove = stack.length > 1 && !(isBase && stack.length === 1);

          return (
            <div
              key={slug}
              className={`theme-stack-item ${draggedIndex === index ? 'dragging' : ''}`}
              draggable={!isLoading}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <GripVertical size={14} className="theme-stack-grip" />

              {/* Color preview */}
              <div className="theme-stack-colors">
                <div
                  className="theme-stack-color"
                  style={{ backgroundColor: theme.preview?.primary }}
                />
                <div
                  className="theme-stack-color"
                  style={{ backgroundColor: theme.preview?.secondary }}
                />
              </div>

              <span className="theme-stack-name">
                {t(theme.nameKey) || slug}
              </span>

              <span className="theme-stack-priority">
                {index === 0 ? t('config.priority_highest') || 'Primary' : ''}
                {index === stack.length - 1 ? t('config.priority_fallback') || 'Fallback' : ''}
              </span>

              {canRemove && (
                <button
                  className="theme-stack-remove"
                  onClick={() => removeFromStack(slug)}
                  disabled={isLoading}
                  title={t('config.remove_from_stack') || 'Remove'}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Available Themes */}
      {availableThemes.length > 0 && (
        <>
          <div className="theme-stack-divider">
            <span>{t('config.available_themes') || 'Available'}</span>
          </div>

          <div className="theme-stack-available">
            {availableThemes.map((theme) => (
              <button
                key={theme.slug}
                className="theme-stack-add-btn"
                onClick={() => addToStack(theme.slug)}
                disabled={isLoading}
              >
                <div className="theme-stack-colors">
                  <div
                    className="theme-stack-color"
                    style={{ backgroundColor: theme.preview?.primary }}
                  />
                  <div
                    className="theme-stack-color"
                    style={{ backgroundColor: theme.preview?.secondary }}
                  />
                </div>
                <span>{t(theme.nameKey) || theme.slug}</span>
                <Plus size={14} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
